import React, { useState, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Typography,
  SelectChangeEvent,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import api from '../services/api';
import { TaskSummary } from '../types';

interface SendToTaskPanelProps {
  tasks: TaskSummary[];
  loadingTasks: boolean;
  selectedIds: string[];
  allResultIds: string[];
  themeColor: string;
  onSuccess?: (count: number) => void;
  onError?: (message: string) => void;
}

const SendToTaskPanel: React.FC<SendToTaskPanelProps> = ({
  tasks,
  loadingTasks,
  selectedIds,
  allResultIds,
  themeColor,
  onSuccess,
  onError,
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState<string>('');
  const [sending, setSending] = useState<boolean>(false);

  const handleSelectChange = useCallback((event: SelectChangeEvent<string>) => {
    setSelectedTaskId(event.target.value);
  }, []);

  const handleSend = useCallback(async () => {
    if (!selectedTaskId) return;

    const idsToSend = selectedIds.length > 0 ? selectedIds : allResultIds;
    if (idsToSend.length === 0) return;

    setSending(true);
    try {
      await api.post(`tasks/samples/taskId/${selectedTaskId}/field/samples/action/push`, {
        samples: idsToSend,
      });
      onSuccess?.(idsToSend.length);
    } catch (err) {
      console.error('Failed to send to task:', err);
      onError?.('Failed to send samples to task');
    } finally {
      setSending(false);
    }
  }, [selectedTaskId, selectedIds, allResultIds, onSuccess, onError]);

  const idsToSendCount = selectedIds.length > 0 ? selectedIds.length : allResultIds.length;
  const buttonText = sending
    ? 'Sending...'
    : selectedIds.length > 0
    ? `Send ${selectedIds.length} Selected`
    : `Send All (${allResultIds.length})`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 200 }} disabled={loadingTasks || sending}>
        <InputLabel>Send to Task</InputLabel>
        <Select
          value={selectedTaskId}
          onChange={handleSelectChange}
          label="Send to Task"
        >
          {loadingTasks ? (
            <MenuItem disabled value="">
              <CircularProgress size={16} sx={{ mr: 1 }} />
              Loading tasks...
            </MenuItem>
          ) : tasks.length === 0 ? (
            <MenuItem disabled value="">No tasks available</MenuItem>
          ) : (
            tasks.map((task) => (
              <MenuItem key={task.id} value={task.id}>
                {task.title}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({task.completedCount + task.noActionCount}/{task.samplesCount})
                </Typography>
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        onClick={handleSend}
        disabled={!selectedTaskId || sending || idsToSendCount === 0}
        sx={{
          bgcolor: themeColor,
          '&:hover': { bgcolor: themeColor, filter: 'brightness(0.9)' },
        }}
        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
      >
        {buttonText}
      </Button>
    </Box>
  );
};

export default SendToTaskPanel;
